import { CSSProperties, FC, ReactNode, useState } from 'react';
import { Avatar, Button, Checkbox, Col, ConfigProvider, Divider, Dropdown, Row, Tag, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { capitalCase } from 'change-case';
import { TbChevronRight, TbCopy, TbDotsVertical, TbExternalLink, TbInfoCircle } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    ActionsDropdownButton,
    DashboardActionsDropdown,
    DropdownButton,
    FolderActionsDropdown,
    InvestmentsActionsDropdown,
    LifecycleActionsDropdown,
    VirtualizedList as List,
    ListActionsDropdown,
    MemosActionsDropdown,
    PipelinesActionsDropdown,
    TeamsActionsDropdown,
    WorkspaceResourceAvatar,
} from '@/shared';
import AttachmentFileAvatar from '@/shared/components/attachments/AttachmentFileAvatar';
import { dayjs, ellipsisStyling, themedCorporateLogoUrl } from '@/utilities';

import SearchResultsSkeleton, {
    SEARCH_RESULTS_SECTION_SKELETON_ROWS,
    type SearchSkeletonDomain,
} from '../../../../components/SearchResultsSkeleton';
import ColumnDropdownFilters from '../../../02-Companies/01-CompaniesSearch/layout/03-Content/views/01-Results/layout/02-ResultsList/components/01-ListHeader/ColumnDropdownFilters';
import {
    companySearchResultsGridCellStyle,
    getCompanySearchResultsGridStyle,
} from '../../../02-Companies/01-CompaniesSearch/layout/03-Content/views/01-Results/layout/02-ResultsList/components/layout';
import type { AllSearchDomain, UnifiedSearchResult } from '../../useAllSearch';

type ResultColumnKey =
    | 'company'
    | 'description'
    | 'email'
    | 'frequency'
    | 'kind'
    | 'location'
    | 'source'
    | 'symbol'
    | 'title'
    | 'updatedAt';

interface DomainColumnConfiguration {
    available: ResultColumnKey[];
    defaults: ResultColumnKey[];
}

const COLUMN_LABELS: Record<ResultColumnKey, string> = {
    company: 'Company',
    description: 'Description',
    email: 'Email',
    frequency: 'Frequency',
    kind: 'Type',
    location: 'Location',
    source: 'Source',
    symbol: 'Symbol',
    title: 'Title / Role',
    updatedAt: 'Updated',
};

const COMMON_COLUMNS: ResultColumnKey[] = ['kind', 'source', 'updatedAt', 'description'];

const DOMAIN_COLUMNS: Record<Exclude<AllSearchDomain, 'companies'>, DomainColumnConfiguration> = {
    documents: {
        available: [...COMMON_COLUMNS, 'company', 'symbol'],
        defaults: ['kind', 'source', 'updatedAt'],
    },
    economic: {
        available: [...COMMON_COLUMNS, 'frequency'],
        defaults: ['kind', 'source', 'frequency'],
    },
    events: {
        available: [...COMMON_COLUMNS, 'company', 'symbol'],
        defaults: ['kind', 'source', 'updatedAt'],
    },
    news: {
        available: [...COMMON_COLUMNS, 'company', 'symbol'],
        defaults: ['kind', 'source', 'updatedAt'],
    },
    people: {
        available: ['company', 'title', 'location', 'email', 'symbol', 'source', 'description'],
        defaults: ['company', 'title', 'location'],
    },
    trade: {
        available: [...COMMON_COLUMNS],
        defaults: ['kind', 'source', 'updatedAt'],
    },
    workspace: {
        available: [...COMMON_COLUMNS],
        defaults: ['kind', 'updatedAt', 'source'],
    },
};

const metadataValue = (result: UnifiedSearchResult, ...labels: string[]) => {
    const normalizedLabels = new Set(labels.map((label) => label.toLowerCase()));

    return result.metadata.find((metadata) => normalizedLabels.has(metadata.label.toLowerCase()))?.value;
};

const resultColumnValue = (result: UnifiedSearchResult, column: ResultColumnKey): ReactNode => {
    const valueByColumn: Record<ResultColumnKey, ReactNode> = {
        company: result.person?.company.name ?? metadataValue(result, 'company', 'issuer') ?? 'N/A',
        description: result.description ?? 'N/A',
        email: result.person?.executive.email ?? metadataValue(result, 'email') ?? 'N/A',
        frequency: metadataValue(result, 'frequency') ?? (result.domain === 'economic' ? 'Current series' : 'N/A'),
        kind: result.kind,
        location: result.person?.executive.location ?? metadataValue(result, 'location', 'city') ?? 'N/A',
        source: result.source,
        symbol: result.person?.company.symbol ?? metadataValue(result, 'symbol', 'ticker') ?? 'N/A',
        title: result.person?.executive.title ?? metadataValue(result, 'title', 'role') ?? result.kind,
        updatedAt: result.updatedAt ? dayjs(result.updatedAt).format('LL') : 'N/A',
    };

    if (column === 'kind' || column === 'source') {
        return <Tag style={{ marginInlineEnd: 0 }}>{valueByColumn[column]}</Tag>;
    }

    return (
        <Typography.Text ellipsis style={{ color: column === 'description' ? undefined : 'inherit' }}>
            {valueByColumn[column]}
        </Typography.Text>
    );
};

const WorkspaceResultActions: FC<{ result: UnifiedSearchResult }> = ({ result }) => {
    const workspaceResource = result.workspaceResource;

    if (!workspaceResource) {
        return null;
    }

    const trigger = <ActionsDropdownButton />;

    switch (workspaceResource.kind) {
        case 'dashboards':
            return <DashboardActionsDropdown dashboard={workspaceResource.value}>{trigger}</DashboardActionsDropdown>;
        case 'folders':
            return <FolderActionsDropdown folder={workspaceResource.value}>{trigger}</FolderActionsDropdown>;
        case 'investments':
            return (
                <InvestmentsActionsDropdown investment={workspaceResource.value}>{trigger}</InvestmentsActionsDropdown>
            );
        case 'lifecycles':
            return <LifecycleActionsDropdown lifecycle={workspaceResource.value}>{trigger}</LifecycleActionsDropdown>;
        case 'lists':
            return <ListActionsDropdown list={workspaceResource.value}>{trigger}</ListActionsDropdown>;
        case 'memos':
            return <MemosActionsDropdown memo={workspaceResource.value}>{trigger}</MemosActionsDropdown>;
        case 'pipelines':
            return <PipelinesActionsDropdown pipeline={workspaceResource.value}>{trigger}</PipelinesActionsDropdown>;
        case 'teams':
            return <TeamsActionsDropdown team={workspaceResource.value}>{trigger}</TeamsActionsDropdown>;
        default:
            return null;
    }
};

export const SearchResultRowsSkeleton: FC<{
    columnCount: number;
    domain?: SearchSkeletonDomain;
    rows?: number;
}> = ({ columnCount, domain = 'companies', rows = SEARCH_RESULTS_SECTION_SKELETON_ROWS }) => (
    <SearchResultsSkeleton columnCount={columnCount} domain={domain} includeHeader={false} rows={rows} />
);

interface DomainResultsTableProps {
    domain: Exclude<AllSearchDomain, 'companies'>;
    loading: boolean;
    onCopy: (result: UnifiedSearchResult) => void;
    onOpen: (result: UnifiedSearchResult) => void;
    onSelectedChange: (id: string, checked: boolean) => void;
    presentationIcon: ReactNode;
    results: UnifiedSearchResult[];
    selectedIds: Set<string>;
    stickyTop?: CSSProperties['top'];
}

const DomainResultsTable: FC<DomainResultsTableProps> = ({
    domain,
    loading,
    onCopy,
    onOpen,
    onSelectedChange,
    presentationIcon,
    results,
    selectedIds,
    stickyTop = 0,
}) => {
    const { resolvedThemeName, token } = useAntDesignTheme();
    const configuration = DOMAIN_COLUMNS[domain];
    const [columns, setColumns] = useState<ResultColumnKey[]>(configuration.defaults);
    const allSelected = results.length > 0 && results.every((result) => selectedIds.has(result.id));
    const someSelected = results.some((result) => selectedIds.has(result.id));
    const selectAll = (checked: boolean) => results.forEach((result) => onSelectedChange(result.id, checked));
    const updateColumn = (index: number, column: string) => {
        setColumns((current) =>
            current.map((value, columnIndex) => (columnIndex === index ? (column as ResultColumnKey) : value)),
        );
    };

    const header = (
        <div
            style={{
                ...getCompanySearchResultsGridStyle(columns),
                backgroundColor: token.colorBgBase,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                padding: '0.25rem',
            }}
        >
            <div style={{ ...companySearchResultsGridCellStyle, alignItems: 'center', display: 'flex' }}>
                <span data-selection-control='true' style={{ display: 'inline-flex', marginLeft: '0.25rem' }}>
                    <Checkbox
                        checked={allSelected}
                        indeterminate={!allSelected && someSelected}
                        onChange={(event) => selectAll(event.target.checked)}
                    />
                </span>
                <Divider type='vertical' />
                <span style={{ color: token.colorTextTertiary, padding: '0 0.25rem' }}>
                    <TbInfoCircle style={{ marginRight: '0.375rem', verticalAlign: '-0.125rem' }} />
                    Search Results
                </span>
            </div>
            {columns.map((column, index) => (
                <div
                    key={`${column}:${index}`}
                    style={{ ...companySearchResultsGridCellStyle, overflow: 'hidden', textAlign: 'right' }}
                >
                    <ColumnDropdownFilters
                        availableColumns={configuration.available}
                        col={column}
                        columnLabels={COLUMN_LABELS}
                        handleMenuClick={updateColumn}
                        index={index}
                        listColumns={columns}
                    >
                        <DropdownButton size='small' style={{ ...ellipsisStyling }} type='text'>
                            {COLUMN_LABELS[column] ?? capitalCase(column)}
                        </DropdownButton>
                    </ColumnDropdownFilters>
                </div>
            ))}
            <div />
        </div>
    );

    return (
        <List
            dataSource={results}
            footer={loading ? <SearchResultRowsSkeleton columnCount={columns.length} domain={domain} /> : undefined}
            getItemKey={(result) => result.id}
            headerStyle={{
                backgroundColor: token.colorBgContainer,
                borderBottom: `1px solid ${token.colorBorder}`,
                boxSizing: 'border-box',
                paddingBlock: '0.5rem',
                paddingInline: '0.5rem',
                position: 'sticky',
                top: stickyTop,
                zIndex: 8,
            }}
            header={header}
            itemLayout='vertical'
            renderItem={(result) => {
                const logoUrl = themedCorporateLogoUrl(
                    result.logoUrl,
                    String(resolvedThemeName).includes('dark') ? 'dark' : 'light',
                );
                const menuItems: MenuProps['items'] = [
                    {
                        icon: result.external ? <TbExternalLink /> : <TbChevronRight />,
                        key: 'open',
                        label: result.external ? 'Open Source' : 'Open',
                        onClick: () => onOpen(result),
                    },
                    ...(result.url
                        ? [
                              {
                                  icon: <TbCopy />,
                                  key: 'copy',
                                  label: 'Copy Link',
                                  onClick: () => onCopy(result),
                              },
                          ]
                        : []),
                ];

                return (
                    <List.Item key={result.id}>
                        <div style={getCompanySearchResultsGridStyle(columns)}>
                            <div style={companySearchResultsGridCellStyle}>
                                <Row align='middle' wrap={false}>
                                    <Col flex='none'>
                                        <span data-selection-control='true' style={{ display: 'inline-flex' }}>
                                            <Checkbox
                                                aria-label={`Select ${result.title}`}
                                                checked={selectedIds.has(result.id)}
                                                onChange={(event) => onSelectedChange(result.id, event.target.checked)}
                                            />
                                        </span>
                                    </Col>
                                    <Col flex='none' style={{ padding: '0 1rem' }}>
                                        {result.workspaceResource ? (
                                            <WorkspaceResourceAvatar
                                                listType={
                                                    result.workspaceResource.kind === 'lists'
                                                        ? result.workspaceResource.value.type
                                                        : undefined
                                                }
                                                resource={result.workspaceResource.kind}
                                                size={40}
                                                src={logoUrl}
                                            />
                                        ) : result.domain === 'documents' ? (
                                            <AttachmentFileAvatar mimeType={result.document?.mimeType ?? ''} />
                                        ) : (
                                            <Avatar
                                                icon={!logoUrl ? presentationIcon : undefined}
                                                shape='square'
                                                size='large'
                                                src={logoUrl}
                                                style={{
                                                    backgroundColor: token.colorBgBase,
                                                    border: `1px solid ${token.colorBorder}`,
                                                    color: token.colorTextTertiary,
                                                }}
                                            />
                                        )}
                                    </Col>
                                    <Col flex='auto' style={{ minWidth: 0 }}>
                                        <ConfigProvider
                                            theme={{ components: { Button: { paddingInline: 0, paddingInlineSM: 0 } } }}
                                        >
                                            <Button
                                                block
                                                disabled={!result.url && !result.person && !result.document}
                                                onClick={() => onOpen(result)}
                                                style={{ display: 'block', height: 'auto', textAlign: 'left' }}
                                                type='link'
                                            >
                                                <Typography.Text ellipsis strong style={{ display: 'block' }}>
                                                    {result.title}
                                                </Typography.Text>
                                            </Button>
                                        </ConfigProvider>
                                        <Typography.Text
                                            ellipsis
                                            style={{
                                                color: token.colorTextTertiary,
                                                display: 'block',
                                                fontSize: token.fontSizeSM,
                                            }}
                                        >
                                            {[result.kind, result.description].filter(Boolean).join(' · ')}
                                        </Typography.Text>
                                    </Col>
                                </Row>
                            </div>
                            {columns.map((column, index) => (
                                <div
                                    key={`${result.id}:${column}:${index}`}
                                    style={{
                                        ...companySearchResultsGridCellStyle,
                                        overflow: 'hidden',
                                        textAlign: 'right',
                                    }}
                                >
                                    {resultColumnValue(result, column)}
                                </div>
                            ))}
                            <div style={{ ...companySearchResultsGridCellStyle, textAlign: 'right' }}>
                                {result.workspaceResource ? (
                                    <WorkspaceResultActions result={result} />
                                ) : (
                                    <Dropdown menu={{ items: menuItems }} placement='bottomRight' trigger={['click']}>
                                        <Button aria-label={`Actions for ${result.title}`} size='small'>
                                            <TbDotsVertical />
                                        </Button>
                                    </Dropdown>
                                )}
                            </div>
                        </div>
                    </List.Item>
                );
            }}
            size='small'
            style={{ backgroundColor: token.colorBgContainer }}
        />
    );
};

export default DomainResultsTable;
